import React, { useEffect } from 'react';
import { getDevConfig } from '../../../config/environment/devConfig';
import { storage } from '../../services/StorageService';
import {
  getMultiplayerDiagnosticsRuntime,
  hasDebugLabelOptIn,
  isDiagForcedUrl,
  resolveInitialMultiplayerLabelsEnabled,
} from '../../diagnostics/multiplayerDiagnostics';

/**
 * MultiplayerLabelsReporter — mount-only bridge that applies the multiplayer
 * debug-label visibility to the diagnostics runtime.
 *
 * Source of truth is the persisted `UserPrefs.dev.showMultiplayerLabels`
 * preference (UIContext + its flag were retired 2026-06-12, cap 1→0). This
 * Reporter is the ONLY legal coupling point: gameplay UI (HUD's 🏷️ toggle)
 * writes the pref through StorageService, and this netobs component applies
 * it to the runtime — the dep-cruiser gate forbids gameplay UI importing
 * diagnostics directly.
 *
 * Mount-time precedence (2026-09-28, U4 — labels are opt-in everywhere,
 * including under Playwright; window.playwrightTest enables nothing):
 *   1. ?diag=1 / ?diagAll=1 force ON over everything;
 *   2. else a non-null preset multiplayerLabelsOverride (wins over both the
 *      persisted preference and the session opt-in);
 *   3. else the persisted preference OR the ?debug=1 / window.forceDebug
 *      session opt-in;
 *   4. else OFF.
 * The same resolution re-runs on every prefs notification, so the game-view
 * mount always stomps a raw runtime seed with the resolved value exactly as
 * the UIContext-era Reporter did. Lives under ui/netobs/ beside the other
 * *Reporters, mounted in main.tsx's game view.
 */
export const MultiplayerLabelsReporter: React.FC = () => {
  useEffect(() => {
    const runtime = getMultiplayerDiagnosticsRuntime();
    const override = getDevConfig().presentation.multiplayerLabelsOverride;
    const diagForced = isDiagForcedUrl();
    const debugOptIn = hasDebugLabelOptIn();
    const applyPreference = (prefEnabled: boolean) => {
      runtime.setLabelsEnabled(resolveInitialMultiplayerLabelsEnabled(
        override,
        prefEnabled,
        diagForced,
        debugOptIn,
      ));
    };

    applyPreference(storage.loadUserPrefs().dev.showMultiplayerLabels);
    return storage.subscribeUserPrefs((prefs) => {
      // Preserve the profile override on every prefs notification. Previously
      // toggling an unrelated preference (notably freeze popups) reapplied the
      // stored `true` value directly and leaked coord frames back into /intro.
      applyPreference(prefs.dev.showMultiplayerLabels);
    });
  }, []);
  return null;
};
